import { Text } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { SideBarItem } from './SideBarItem';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { getSelectedAccountsSubject, getUserSubject, isReportsPageEnabled } from '../../../store/User';
import { HIDE_NAV_PAGES } from '../../../utils/helpers';
import { Account, User } from '../../../types';
import { useSession } from '../../../Api/useSession';
import { PermissibleRender } from '../PermissibleRender';
import { Permission } from '../../../store/GeneralStore';
import { defaultFeatureFlags, FeatureFlags, getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { hasAdviseView, hasFeatureAccess } from '../../../store/User';

export const SideNavBar = () => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [showSideBar, setShowSideBar] = useState<boolean>(false);
  const [selectedAccounts, setSelectedAccounts] = useState<Account[] | null>();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);
  const location = useLocation();
  const name = location.pathname.replace('/', '');
  const [user, setUser] = useState<User | undefined>(undefined);
  const { signOut } = useSession();

  useEffect(() => {
    if (user) {
      setShowSideBar(!HIDE_NAV_PAGES.includes(name));
    }
  }, [user, name, selectedAccounts?.length]);

  useEffect(() => {
    const userSub = getUserSubject().subscribe((user) => setUser(user));
    const acctSub = getSelectedAccountsSubject().subscribe((accts) => {
      setSelectedAccounts(accts);
    });
    const featureFlagsSub = getFeatureFlagSubject().subscribe((flags) => setFeatureFlags(flags));

    return () => {
      if (userSub) userSub.unsubscribe();
      if (acctSub) acctSub.unsubscribe();
      if (featureFlagsSub) featureFlagsSub.unsubscribe();
    };
  }, []);

  const changeExpanded = (): void => setExpanded(!expanded);
  const handleLogOut = () => signOut();

  if (!showSideBar) return <></>;

  return (
    <>
      <div className="sidebar-wrapper z-10 max-h-0">
        <div
          className={classNames('sidebar min-h-screen border-r hover:shadow-lg min-w bg-nav-50', {
            'w-[3.35rem]': !expanded,
            'w-52 px-3': expanded
          })}
          data-cy="sidebar"
          data-testid="sidebar">
          <div className="flex h-screen flex-col justify-between pb-6">
            <div>
              <div className="py-4 pl-3.5 border-b border-nav-400 w-full overflow-hidden relative">
                <Link
                  className="ml-auto min-w-[10rem] block with-rectangle after:bg-nav-50 after:w-3"
                  title="Home"
                  to={'/'}>
                  <img
                    src={'/UPSTACK-Logo.svg'}
                    className="mr-3 h-10"
                    alt="UPSTACK Logo"
                  />
                </Link>
              </div>
              <ul className="space-y-2 tracking-wide z-20 relative">
                <SideBarItem
                  navLinkProps={{ to: '' }}
                  iconProps={{ type: 'homeIcon' }}
                  textProps={{ children: 'Home' }}
                  active={name === ''}
                  dataCy="home-sidebar"
                  key="home-sidebar"
                />
                {hasFeatureAccess('inventory') && (
                  <SideBarItem
                    navLinkProps={{ to: 'orders' }}
                    iconProps={{ type: 'ordersIcon' }}
                    textProps={{ children: 'Orders' }}
                    active={name === 'orders'}
                    dataCy="orders-sidebar"
                    key="orders-sidebar"
                  />
                )}
                {featureFlags.projects && (
                  <PermissibleRender
                    requiredPermissions={[Permission.VIEW_PROJECTS]}
                    key="projects-sidebar">
                    <SideBarItem
                      navLinkProps={{ to: 'projects' }}
                      iconProps={{ type: 'projectsIcon' }}
                      textProps={{ children: 'Projects' }}
                      active={name === 'projects'}
                      dataCy="projects-sidebar"
                    />
                  </PermissibleRender>
                )}
                {hasFeatureAccess('inventory') && (
                  <SideBarItem
                    navLinkProps={{ to: 'inventory' }}
                    iconProps={{ type: 'inventoryIcon' }}
                    textProps={{ children: 'Inventory' }}
                    active={name === 'inventory'}
                    dataCy="inventory-sidebar"
                    key="inventory-sidebar"
                  />
                )}
                <PermissibleRender
                  key="support-cases-sidebar"
                  requiredPermissions={[Permission.VIEW_CASES]}>
                  <SideBarItem
                    navLinkProps={{ to: 'support-cases' }}
                    iconProps={{ type: 'casesIcon' }}
                    textProps={{ children: 'Support Cases' }}
                    active={name === 'support-cases'}
                    dataCy="support-cases-sidebar"
                  />
                </PermissibleRender>
                {isReportsPageEnabled(featureFlags) && hasFeatureAccess('inventory') && (
                  <SideBarItem
                    navLinkProps={{ to: 'reports' }}
                    iconProps={{ type: 'reportsIcon' }}
                    textProps={{ children: 'Reports' }}
                    active={name === 'reports'}
                    dataCy="reports-sidebar"
                    key="reports-sidebar"
                  />
                )}
                <div className="-mb-5 border-t w-full flex border-nav-400"></div>
                <SideBarItem
                  navLinkProps={{ to: 'my-team' }}
                  iconProps={{ type: 'myTeam' }}
                  textProps={{ children: 'My Team' }}
                  active={name === 'my-team'}
                  dataCy="my-team-sidebar"
                />
                {featureFlags.price_benchmarking && (
                  <PermissibleRender requiredPermissions={[Permission.VIEW_PRICE_BENCHMARK]}>
                    <SideBarItem
                      key="cost-intelligence-sidebar"
                      navLinkProps={{ to: 'cost-intelligence' }}
                      iconProps={{ type: 'analyticsIcon' }}
                      textProps={{ children: 'Cost Intelligence' }}
                      active={name === 'cost-intelligence'}
                      dataCy="cost-intelligence-sidebar"
                    />
                  </PermissibleRender>
                )}
                {featureFlags.thoughtspot_poc && (
                  <PermissibleRender requiredPermissions={[Permission.VIEW_THOUGHTSPOT]}>
                    <SideBarItem
                      key="analytics-sidebar"
                      navLinkProps={{ to: 'analytics' }}
                      iconProps={{ type: 'costAnalyticsIcon' }}
                      textProps={{ children: 'Cost Analytics' }}
                      active={name === 'analytics'}
                      dataCy="analytics-sidebar"
                    />
                  </PermissibleRender>
                )}
                {featureFlags.hex_embed && (
                  <PermissibleRender requiredPermissions={[Permission.VIEW_THOUGHTSPOT]}>
                    <SideBarItem
                      key="hex-analytics-sidebar"
                      navLinkProps={{ to: 'hex-analytics' }}
                      iconProps={{ type: 'analyticsIcon' }}
                      textProps={{ children: 'Analytics (Hex)' }}
                      active={name === 'hex-analytics'}
                      dataCy="hex-analytics-sidebar"
                    />
                  </PermissibleRender>
                )}
                {featureFlags.advise && hasAdviseView() && (
                  <>
                    <div className="-mb-5 border-t w-full flex border-nav-400"></div>
                    <SideBarItem
                      key="advise-sidebar"
                      navLinkProps={{ to: 'advise' }}
                      iconProps={{ type: 'analyticsIcon' }}
                      textProps={{ children: 'Advise' }}
                      active={name.startsWith('advise')}
                      dataCy="advise-sidebar"
                    />
                  </>
                )}
              </ul>
            </div>
            <div className="-mb-5 border-t w-full flex border-nav-400">
              <button
                className="bg group flex items-center space-x-4 m-2 p-2 pl-1 text-gray-600 hover:bg-nav-100 w-full overflow-hidden whitespace-nowrap hover:min-w-max rounded"
                onClick={handleLogOut}>
                <Icon type="logoutIcon" />
                <Text
                  dataCy="logout-button"
                  dataTestId="logout-button"
                  className="group-hover:text-nav-200 text-nav-200"
                  color="black"
                  size="base"
                  weight="normal">
                  Log Out
                </Text>
              </button>
            </div>
          </div>
          <button
            className={classNames('expand-button arrow-sidebar z-10', { 'reverse-button': !expanded })}
            onClick={changeExpanded}
          />
        </div>
        <button
          aria-label="expand-button-external"
          className={classNames('expand-button arrow-sidebar', { 'reverse-button': !expanded })}
          onClick={changeExpanded}
        />
      </div>
    </>
  );
};
